// frontend/src/samkalp-admin/tabs/CourseMappingTab.js
//
// Duplicated from frontend/src/pages/AskOSAdmin.js's CourseMappingTab (not exported there).
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Row, Col, Form, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { Link2, Plus, Trash2 } from 'lucide-react';
import api from '../../api/api';
import { StatusBadge, EmptyRow, useAlert } from './shared';

const CourseMappingTab = ({ slug }) => {
    const [mappings, setMappings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [alert, showAlert]      = useAlert();
    const [form, setForm]         = useState({ lms_provider: 'moodle', lms_course_id: '', lms_course_name: '', project_id: '' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, p] = await Promise.all([
                api.get(`/tenant/${slug}/askos/admin/lms-mappings`),
                api.get(`/tenant/${slug}/askos/admin/projects`),
            ]);
            setMappings(m.data); setProjects(p.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!form.lms_course_id || !form.project_id) return showAlert('Course ID and Project are required.', 'danger');
        setSaving(true);
        try {
            await api.post(`/tenant/${slug}/askos/admin/lms-mappings`,
                { ...form, project_id: parseInt(form.project_id) });
            showAlert('Course mapping created.');
            setForm(f => ({ ...f, lms_course_id: '', lms_course_name: '' }));
            load();
        } catch (e) { showAlert(e.response?.data?.detail || 'Failed to create mapping.', 'danger'); }
        setSaving(false);
    };

    const remove = async (id) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/lms-mappings/${id}`);
            showAlert('Mapping removed.', 'warning');
            load();
        } catch (e) { showAlert(e.response?.data?.detail || 'Failed to remove.', 'danger'); }
    };

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}
            <p className="text-muted small mb-4">
                Map your LMS course IDs to Academicos projects. Once SSO is live, enrolled students will automatically get AskOS access.
            </p>

            <Card className="faculty-card mb-4">
                <Card.Body className="p-4">
                    <p className="x-small fw-bold text-muted text-uppercase mb-3">Add New Mapping</p>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">LMS Provider</Form.Label>
                            <Form.Select value={form.lms_provider} onChange={e => setForm(f => ({ ...f, lms_provider: e.target.value }))} className="shadow-none">
                                {['moodle','canvas','blackboard','sap','custom'].map(p => (
                                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Course ID <span className="text-danger">*</span></Form.Label>
                            <Form.Control placeholder="e.g. CS301-2026-S1" value={form.lms_course_id} onChange={e => setForm(f => ({ ...f, lms_course_id: e.target.value }))} className="shadow-none" />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Course Name</Form.Label>
                            <Form.Control placeholder="Data Structures" value={form.lms_course_name} onChange={e => setForm(f => ({ ...f, lms_course_name: e.target.value }))} className="shadow-none" />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Project <span className="text-danger">*</span></Form.Label>
                            <Form.Select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} className="shadow-none">
                                <option value="">Select…</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.source === 'inherited' ? ' (Samkalp)' : ''}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={1} className="text-end">
                            <Button variant="primary" className="w-100 d-flex align-items-center justify-content-center gap-1 rounded-pill" onClick={add} disabled={saving}>
                                {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : <><Plus size={14} /> Add</>}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="faculty-card">
                <Table responsive hover className="mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Provider</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Course ID</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Course Name</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Maps to Project</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Status</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 pe-4 text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                        ) : mappings.length === 0 ? (
                            <EmptyRow cols={6} icon={Link2} message="No mappings yet. Add your first course mapping above." />
                        ) : mappings.map(m => {
                            const proj = projects.find(p => p.id === m.project_id);
                            return (
                                <tr key={m.id}>
                                    <td className="py-3 px-4"><Badge className="badge-soft-primary text-uppercase x-small">{m.lms_provider || 'custom'}</Badge></td>
                                    <td className="py-3 align-middle"><code className="x-small bg-light px-2 py-1 rounded">{m.lms_course_id}</code></td>
                                    <td className="py-3 align-middle text-muted">{m.lms_course_name || '—'}</td>
                                    <td className="py-3 align-middle"><span className="text-primary fw-semibold">{proj?.name || `#${m.project_id}`}</span></td>
                                    <td className="py-3 align-middle"><StatusBadge status={m.is_active ? 'active' : 'inactive'} /></td>
                                    <td className="py-3 pe-4 align-middle text-end">
                                        <Button variant="outline-danger" size="sm" className="d-inline-flex align-items-center gap-1 rounded-pill" onClick={() => remove(m.id)}>
                                            <Trash2 size={12} /> Remove
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Card>
        </>
    );
};

export default CourseMappingTab;
